import React from "react";
import { Box } from "@mui/material";
import { dt } from "../../../shared/themes/redesignTokens";
import { WorkOrderPriorityCode } from "../models/models";
import useTranslation from "../../../shared/hooks/useTranslation";

export const priorityColors: Record<number, string> = {
  [WorkOrderPriorityCode.Critical]: "#D4572B",  // spec: priority-critical (terracotta)
  [WorkOrderPriorityCode.High]: "#E8A817",      // spec: priority-high (amber)
  [WorkOrderPriorityCode.Normal]: "#E8E5DF",    // spec: priority-normal (subtle)
  [WorkOrderPriorityCode.Low]: "#3B82F6",       // spec: priority-low (blue)
};

const priorityTranslationKeys: Record<number, string> = {
  [WorkOrderPriorityCode.Critical]: "priorities.critical",
  [WorkOrderPriorityCode.High]: "priorities.high",
  [WorkOrderPriorityCode.Normal]: "priorities.normal",
  [WorkOrderPriorityCode.Low]: "priorities.low",
};

interface WorkOrderPriorityBadgeProps {
  priorityCode: number;
  size?: "sm" | "lg";
}

const WorkOrderPriorityBadge = ({ priorityCode, size = "sm" }: WorkOrderPriorityBadgeProps) => {
  const { translate } = useTranslation("operations");
  const color = priorityColors[priorityCode] || priorityColors[WorkOrderPriorityCode.Normal];
  const key = priorityTranslationKeys[priorityCode];
  const label = key ? translate(key) : "Normal";
  const isLg = size === "lg";

  // Badge background at 10% opacity of the priority color
  const badgeBgMap: Record<number, string> = {
    [WorkOrderPriorityCode.Critical]: "rgba(212,87,43,0.1)",
    [WorkOrderPriorityCode.High]: "rgba(232,168,23,0.1)",
    [WorkOrderPriorityCode.Normal]: "rgba(232,229,223,0.3)",
    [WorkOrderPriorityCode.Low]: "rgba(59,130,246,0.08)",
  };
  const lgBgMap: Record<number, string> = {
    [WorkOrderPriorityCode.Critical]: "#FEF3F0",
    [WorkOrderPriorityCode.High]: "#FFF3E0",
    [WorkOrderPriorityCode.Normal]: "#F4F6F9",
    [WorkOrderPriorityCode.Low]: "#EBF5FB",
  };
  const badgeTextMap: Record<number, string> = {
    [WorkOrderPriorityCode.Critical]: "#D4572B",
    [WorkOrderPriorityCode.High]: "#C08E12",
    [WorkOrderPriorityCode.Normal]: "#8C9099",
    [WorkOrderPriorityCode.Low]: "#3B82F6",
  };
  const badgeBg = isLg
    ? (lgBgMap[priorityCode] || lgBgMap[WorkOrderPriorityCode.Normal])
    : (badgeBgMap[priorityCode] || badgeBgMap[WorkOrderPriorityCode.Normal]);
  const badgeText = badgeTextMap[priorityCode] || badgeTextMap[WorkOrderPriorityCode.Normal];

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: isLg ? "6px" : undefined,
        padding: isLg ? "4px 12px" : "2px 8px",
        borderRadius: isLg ? "6px" : "4px",
        backgroundColor: badgeBg,
        fontSize: isLg ? "12px" : "10px",
        fontWeight: 600,
        fontFamily: dt.font,
        color: badgeText,
        whiteSpace: "nowrap",
        lineHeight: isLg ? "16px" : "12px",
        letterSpacing: isLg ? "0.04em" : "0.2px",
        textTransform: isLg ? "uppercase" as const : undefined,
        transition: "all 0.2s ease",
      }}
    >
      {isLg && (
        <Box
          sx={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: color,
            flexShrink: 0,
          }}
        />
      )}
      {label}
    </Box>
  );
};

export default WorkOrderPriorityBadge;
